import { apiSlice } from '../../../store/apiSlice';
import { User, CreateUserPayload, UpdateUserPayload, UserListResponse, ChangePasswordPayload } from '../types';
import { Logger } from '../../../lib/utils';

// Definir interfaces para respuestas del API
interface ApiUserResponse {
  usuarios?: User[];
  total?: number;
  [key: string]: unknown;
}

// Manejador de errores genérico para endpoints de usuarios
const handleApiError = (error: unknown, endpoint: string) => {
  Logger.error(`Error en ${endpoint}:`, error);
  return error;
};

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Obtener todos los usuarios (con paginación)
    getUsers: builder.query<UserListResponse, { page?: number, limit?: number, empresaId?: number } | undefined>({
      query: (params = { page: 1, limit: 10 }) => {
        // Si hay un empresaId, usamos la ruta de usuarios de esa empresa
        if (params.empresaId) {
          return {
            url: `/empresas/${params.empresaId}/usuarios`,
            params: {
              page: params.page || 1,
              limit: params.limit || 10
            },
          };
        }
        // Si somos admin, obtenemos todos los usuarios
        return {
          url: '/usuarios',
          params: {
            page: params.page || 1,
            limit: params.limit || 10
          },
        };
      },
      transformResponse: (response: User[] | ApiUserResponse, meta, arg) => {
        Logger.api('Respuesta de getUsers:', response);
        
        if (Array.isArray(response)) {
          return {
            users: response,
            total: response.length,
            page: arg?.page || 1,
            limit: arg?.limit || 10
          };
        }
        
        if (response && 'usuarios' in response) {
          return {
            users: response.usuarios || [],
            total: response.total || response.usuarios?.length || 0,
            page: arg?.page || 1,
            limit: arg?.limit || 10
          };
        }
        
        // Fallback para cuando no hay datos
        return {
          users: [],
          total: 0,
          page: arg?.page || 1,
          limit: arg?.limit || 10
        };
      },
      transformErrorResponse: (error) => handleApiError(error, 'getUsers'),
      providesTags: (result) =>
        result && result.users
          ? [
              ...result.users.map(({ id }) => ({ type: 'User' as const, id })),
              { type: 'User' as const, id: 'LIST' },
            ]
          : [{ type: 'User' as const, id: 'LIST' }],
    }),

    // Obtener un usuario por ID
    getUserById: builder.query<User, number>({
      query: (id) => `/usuarios/${id}`,
      transformErrorResponse: (error) => handleApiError(error, 'getUserById'),
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    // Verificar si existe un email
    checkEmail: builder.query<{ exists: boolean }, string>({
      query: (email) => `/auth/check-email?email=${encodeURIComponent(email)}`,
      transformErrorResponse: (error) => handleApiError(error, 'checkEmail'),
    }),

    // Crear un nuevo usuario
    createUser: builder.mutation<User, CreateUserPayload>({
      query: (userData) => {
        // Asegurar que empresaId sea siempre un número
        const processedData = {
          ...userData,
          empresaId: userData.empresaId ? Number(userData.empresaId) : undefined
        };
        
        Logger.api('Enviando datos de usuario al servidor:', processedData);
        
        return {
          url: '/auth/register',
          method: 'POST',
          body: processedData,
        };
      },
      transformErrorResponse: (error) => handleApiError(error, 'createUser'),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    // Actualizar un usuario existente
    updateUser: builder.mutation<User, { id: number; changes: UpdateUserPayload }>({
      query: ({ id, changes }) => ({
        url: `/usuarios/${id}`,
        method: 'PATCH',
        body: changes,
      }),
      transformErrorResponse: (error) => handleApiError(error, 'updateUser'),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' }
      ],
    }),

    // Eliminar un usuario
    deleteUser: builder.mutation<{ success: boolean; id: number }, number>({
      query: (id) => ({
        url: `/usuarios/${id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: (error) => handleApiError(error, 'deleteUser'),
      invalidatesTags: (result, error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' }
      ],
    }),
    
    // Cambiar contraseña (usuario actual)
    changePassword: builder.mutation<{ message: string }, ChangePasswordPayload>({
      query: (passwordData) => ({
        url: '/auth/change-password',
        method: 'POST',
        body: passwordData,
      }),
      transformErrorResponse: (error) => handleApiError(error, 'changePassword'),
    }),

    // Obtener el usuario actual
    getCurrentUser: builder.query<User, void>({
      query: () => '/auth/me',
      transformErrorResponse: (error) => handleApiError(error, 'getCurrentUser'),
      providesTags: (result) => (result ? [{ type: 'User', id: result.id }] : []),
    }),
  }),
  overrideExisting: false,
});

// Exportar los hooks generados para usar en componentes
export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useChangePasswordMutation,
  useGetCurrentUserQuery,
  useCheckEmailQuery,
  useLazyCheckEmailQuery,
} = usersApiSlice; 